import React from 'react';
import { Box, Typography, Button, Grid2, Stack, Container, Card, CardHeader, CardContent, Toolbar, Divider, BottomNavigationAction, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SpeedIcon from '@mui/icons-material/Speed';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LaptopIcon from '@mui/icons-material/Laptop';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { AppName, LogoLargeScreen } from '../constants/Constants';
import Footer from '../components/footer/Footer';
import EmailIcon from '@mui/icons-material/Email';

const Home = () => {
    const navigate = useNavigate();
    const [expandedPreview, setExpandedPreview] = React.useState(null);

    const handleLogin = (role) => {
        navigate('/login', { replace: false });
    };

    const handleGetStarted = () => {
        navigate('/signup', { replace: false });
    };

    const renderExpandedContent = () => {
        if (expandedPreview === 'accuracy') {
            return (
                <Box
                    component="img"
                    src="/AccuracyTrendPreview.png"
                    alt="Accuracy trend graph preview"
                    sx={{ width: '100%', height: 'auto', borderRadius: 1.5, objectFit: 'contain' }}
                />
            );
        }
        if (expandedPreview === 'level') {
            return (
                <Box
                    component="img"
                    src="/LevelTrendPreview.png"
                    alt="Level trend graph preview"
                    sx={{ width: '100%', height: 'auto', borderRadius: 1.5, objectFit: 'contain' }}
                />
            );
        }
        return (
            <Stack spacing={1.5}>
                <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'action.hover' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Performance feedback
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Your potential is clear, with a strong 70% accuracy in one test. Current performance fluctuates, so the key is maintaining steady focus across all sessions.
                    </Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'action.hover' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Key observation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Incorrect answers are often completed faster than correct ones, which can be a sign of rushing. With better pacing, students are able to handle intermediate and advanced questions more effectively.
                    </Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'action.hover' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Focus areas
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Slow down on difficult questions, review errors from weaker tests, and repeat the calm approach from your best session to improve consistency.
                    </Typography>
                </Box>
            </Stack>
        );
    };


    return (
        <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
            <Toolbar />

            {/* Header */}
            <Divider color="white" />
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(180deg, #0FA4AF 0%, #1FB6C1 35%, #5BAEE6 100%)',
                    color: 'white',
                    py: { xs: 6, md: 10 },
                    px: { xs: 2, md: 4 },
                    position: 'relative',

                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <Box
                            sx={{
                                flex: { xs: '1 1 100%', md: '1 1 50%' },
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                textAlign: 'center',
                                width: '100%',
                            }}
                        >
                            <Typography
                                variant="h2"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    mb: 2,
                                    fontSize: { xs: '2rem', md: '3rem' },
                                }}
                            >
                                Practice that adapts to every child.
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    mb: 4,
                                    opacity: 0.9,
                                    fontSize: { xs: '1rem', md: '1.25rem' },
                                }}
                            >
                                Better practice — at the right level, at the right pace.
                            </Typography>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                sx={{
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={(e) => { e.preventDefault(); handleLogin() }}
                                    sx={{
                                        backgroundColor: '#1565C0',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#0D47A1',
                                        },
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 32px !important',
                                        fontSize: '1rem !important',
                                        minHeight: 'auto',
                                    }}
                                >
                                    Login
                                </Button>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={(e) => { e.preventDefault(); handleGetStarted() }}
                                    sx={{
                                        backgroundColor: '#FB8C00',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#F57C00',
                                        },
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 32px !important',
                                        fontSize: '1rem !important',
                                        minHeight: 'auto',
                                    }}
                                >
                                    Get Started
                                </Button>
                            </Stack>
                        </Box>
                        <Box
                            sx={{
                                flex: { xs: '1 1 100%', md: '1 1 50%' },
                                display: 'flex',
                                justifyContent: { xs: 'center', md: 'flex-end' },
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                component="img"
                                src="/studentStudying.png"
                                alt="Student studying"
                                sx={{
                                    width: { xs: '100%', sm: '90%', md: '85%', lg: '80%' },
                                    maxWidth: { xs: '400px', sm: '450px', md: '500px' },
                                    height: 'auto',
                                    borderRadius: 2.5,
                                    objectFit: 'contain',
                                }}
                            />
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Why Practice Needs to Change */}
            <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 4 }, backgroundColor: 'background.paper' }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            mb: 4,
                            color: 'text.primary',
                        }}
                    >
                        Why Practice Needs to Change
                    </Typography>
                    <Grid2 container spacing={4} sx={{ mb: 4, justifyContent: 'center' }}>
                        <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Stack spacing={2} sx={{ alignItems: 'center', width: '100%', maxWidth: '400px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', width: '100%' }}>
                                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                                    <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center' }}>
                                        Same questions for every child
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', width: '100%' }}>
                                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                                    <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center' }}>
                                        Too easy for some, too hard for others
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Stack spacing={2} sx={{ alignItems: 'center', width: '100%', maxWidth: '400px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', width: '100%' }}>
                                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                                    <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center' }}>
                                        Mistakes repeat without correction
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', width: '100%' }}>
                                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                                    <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center' }}>
                                        Speed only judged in exams
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid2>
                    </Grid2>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            fontStyle: 'italic',
                            color: 'text.secondary',
                        }}
                    >
                        Practice should prepare children to perform – not make them anxious.
                    </Typography>
                </Container>
            </Box>
            <Divider sx={{ borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' }} />

            {/* How AdaptPrepare Helps */}
            <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 4 }, backgroundColor: 'background.default' }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            mb: 6,
                            color: 'text.primary',
                        }}
                    >
                        How AdaptPrepare Helps
                    </Typography>
                    <Grid2 container spacing={4} sx={{ justifyContent: 'center' }}>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
                            <Card
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    textAlign: 'center',
                                    backgroundColor: 'background.paper',
                                    boxShadow: (theme) => theme.palette.mode === 'dark'
                                        ? '0 2px 8px rgba(0,0,0,0.5)'
                                        : '0 2px 8px rgba(0,0,0,0.1)',
                                }}
                            >
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box
                                        sx={{
                                            height: { xs: '120px', sm: '140px', md: '160px' },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src="/QuestionMatch.png"
                                            alt="QuestionMatch"
                                            sx={{
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                                height: 'auto',
                                                width: 'auto',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center', mt: 'auto' }}>
                                        Questions match the student's level.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    textAlign: 'center',
                                    backgroundColor: 'background.paper',
                                    boxShadow: (theme) => theme.palette.mode === 'dark'
                                        ? '0 2px 8px rgba(0,0,0,0.5)'
                                        : '0 2px 8px rgba(0,0,0,0.1)',
                                }}
                            >
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box
                                        sx={{
                                            height: { xs: '120px', sm: '140px', md: '160px' },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src="/Difficulty.png"
                                            alt="Difficulty"
                                            sx={{
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                                height: 'auto',
                                                width: 'auto',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center', mt: 'auto' }}>
                                        Difficulty increases gradually.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    textAlign: 'center',
                                    backgroundColor: 'background.paper',
                                    boxShadow: (theme) => theme.palette.mode === 'dark'
                                        ? '0 2px 8px rgba(0,0,0,0.5)'
                                        : '0 2px 8px rgba(0,0,0,0.1)',
                                }}
                            >
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box
                                        sx={{
                                            height: { xs: '120px', sm: '140px', md: '160px' },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src="/Feedback.png"
                                            alt="Feedback"
                                            sx={{
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                                height: 'auto',
                                                width: 'auto',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center', mt: 'auto' }}>
                                        Feedback on mistakes.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    textAlign: 'center',
                                    backgroundColor: 'background.paper',
                                    boxShadow: (theme) => theme.palette.mode === 'dark'
                                        ? '0 2px 8px rgba(0,0,0,0.5)'
                                        : '0 2px 8px rgba(0,0,0,0.1)',
                                }}
                            >
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box
                                        sx={{
                                            height: { xs: '120px', sm: '140px', md: '160px' },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src="/Speed.png"
                                            alt="Speed"
                                            sx={{
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                                height: 'auto',
                                                width: 'auto',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center', mt: 'auto' }}>
                                        Speed observed, not forced.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    textAlign: 'center',
                                    backgroundColor: 'background.paper',
                                    boxShadow: (theme) => theme.palette.mode === 'dark'
                                        ? '0 2px 8px rgba(0,0,0,0.5)'
                                        : '0 2px 8px rgba(0,0,0,0.1)',
                                }}
                            >
                                <CardContent>
                                    <Box
                                        component="img"
                                        src="/Independently.png"
                                        alt="Independent"
                                        sx={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            height: 'auto',
                                            width: 'auto',
                                            objectFit: 'contain',
                                        }}
                                    />
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                        Practice independently.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    textAlign: 'center',
                                    backgroundColor: 'background.paper',
                                    boxShadow: (theme) => theme.palette.mode === 'dark'
                                        ? '0 2px 8px rgba(0,0,0,0.5)'
                                        : '0 2px 8px rgba(0,0,0,0.1)',
                                }}
                            >
                                <CardContent>
                                    <Box
                                        component="img"
                                        src="/Monitor.png"
                                        alt="Monitor"
                                        sx={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            height: 'auto',
                                            width: 'auto',
                                            objectFit: 'contain',
                                        }}
                                    />
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                        Parents & teachers monitor progress.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid2>
                    </Grid2>
                </Container>
            </Box>

            <Divider sx={{ borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' }} />

            {/* How To Use + Adaptive Self-Test */}
            <Box sx={{ py: { xs: 5, md: 6 }, px: { xs: 2, md: 4 }, backgroundColor: 'background.default' }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            mb: 1,
                            color: 'text.primary',
                        }}
                    >
                        How It Works
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: 'text.secondary',
                            mb: 3,
                        }}
                    >
                        Follow the setup flow once, then use adaptive self-tests and assignments to guide practice.
                    </Typography>
                    <Grid2 container spacing={3} justifyContent="center">
                        <Grid2 size={{ xs: 12, md: 5.5 }}>
                            <Card sx={{ height: '100%', borderRadius: 3 }}>
                                <CardHeader title="Quick start flow"  sx={{ py: 0.75 }} />
                                <CardContent sx={{ pt: 1.25, pb: 1.5 }}>
                                    <Stack spacing={1.1}>
                                        <Typography variant="body2" color="text.secondary">1. Create your account and log in.</Typography>
                                        <Typography variant="body2" color="text.secondary">2. Enroll your student and complete activation.</Typography>
                                        <Typography variant="body2" color="text.secondary">3. Start adaptive self-tests or create an assignment.</Typography>
                                        <Typography variant="body2" color="text.secondary">4. Review trends and AI insights after practice.</Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid2>
                        <Grid2 display="none" size={{ xs: 12, md: 5.5 }}>
                            <Card sx={{ height: '100%', borderRadius: 3 }}>
                                <CardHeader title="Adaptive Self-Test" subheader="What makes it effective" sx={{ py: 0.75 }} />
                                <CardContent sx={{ pt: 1.25, pb: 1.5 }}>
                                    <Stack spacing={1.1}>
                                        <Typography variant="body2" color="text.secondary">Questions adjust to each student’s response in real time.</Typography>
                                        <Typography variant="body2" color="text.secondary">Difficulty stays balanced to avoid both boredom and overload.</Typography>
                                        <Typography variant="body2" color="text.secondary">Practice improves understanding, pacing, and consistency.</Typography>
                                        <Typography variant="body2" color="text.secondary">Built to guide learning — not to replace classroom teaching.</Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid2>
                    </Grid2>
                </Container>
            </Box>

            <Divider sx={{ borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' }} />

            {/* See What You Get */}
            <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 4 }, backgroundColor: 'background.paper' }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            mb: 1.5,
                            color: 'text.primary',
                        }}
                    >
                        See What You Get
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: 'text.secondary',
                            mb: 5,
                        }}
                    >
                        Sample dashboard and AI insights you receive after practice.
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 3,
                            mb: 3,
                        }}
                    >
                        <Card sx={{ height: '100%', borderRadius: 3, cursor: 'pointer' }} onClick={() => setExpandedPreview('accuracy')}>
                            <CardHeader title="Accuracy Trend" subheader="Sample graph" sx={{ py: 0.75 }} />
                            <CardContent>
                                <Box
                                    component="img"
                                    src="/AccuracyTrendPreview.png"
                                    alt="Accuracy trend graph preview"
                                    sx={{
                                        width: '100%',
                                        height: 165,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        objectFit: 'contain',
                                    }}
                                />
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', textAlign: 'right' }}>
                                    Click to expand
                                </Typography>
                            </CardContent>
                        </Card>

                        <Card sx={{ height: '100%', borderRadius: 3, cursor: 'pointer' }} onClick={() => setExpandedPreview('level')}>
                            <CardHeader title="Level Trend" subheader="Sample graph" sx={{ py: 0.75 }} />
                            <CardContent>
                                <Box
                                    component="img"
                                    src="/LevelTrendPreview.png"
                                    alt="Level trend graph preview"
                                    sx={{
                                        width: '100%',
                                        height: 165,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        objectFit: 'contain',
                                    }}
                                />
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', textAlign: 'right' }}>
                                    Click to expand
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    <Grid2 container spacing={3} sx={{ justifyContent: 'center' }}>
                        <Grid2 size={{ xs: 12 }}>
                            <Card sx={{ height: '100%', borderRadius: 3, cursor: 'pointer' }} onClick={() => setExpandedPreview('ai')}>
                                <CardHeader title="AI Analysis Snapshot" subheader="Sample insights" sx={{ py: 0.75 }} />
                                <CardContent sx={{ pt: 1, pb: 1.25 }}>
                                    <Stack spacing={1}>
                                        <Box sx={{ p: 1, borderRadius: 1.5, backgroundColor: 'action.hover' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.25, display: 'block' }}>
                                                Performance feedback
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    lineHeight: 1.35,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                Your potential is clear, with a strong 70% accuracy in one test. Current performance fluctuates, so the key is maintaining steady focus across all sessions.
                                            </Typography>
                                        </Box>
                                        <Box sx={{ p: 1, borderRadius: 1.5, backgroundColor: 'action.hover' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.25, display: 'block' }}>
                                                Key observation
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    lineHeight: 1.35,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                Incorrect answers are often completed faster than correct ones, which can be a sign of rushing. With better pacing, students are able to handle intermediate and advanced questions more effectively.
                                            </Typography>
                                        </Box>
                                        <Box sx={{ p: 1, borderRadius: 1.5, backgroundColor: 'action.hover' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.25, display: 'block' }}>
                                                Focus areas
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    lineHeight: 1.35,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                Slow down on difficult questions, review errors from weaker tests, and repeat the calm approach from your best session to improve consistency.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', textAlign: 'right' }}>
                                        Click to expand
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid2>
                    </Grid2>

                    <Typography
                        variant="caption"
                        sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mt: 3 }}
                    >
                        Sample preview only. Actual insights are personalized for each student.
                    </Typography>
                </Container>
            </Box>

            <Dialog
                open={Boolean(expandedPreview)}
                onClose={() => setExpandedPreview(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box component="span">
                        {expandedPreview === 'accuracy' && 'Accuracy Trend'}
                        {expandedPreview === 'level' && 'Level Trend'}
                        {expandedPreview === 'ai' && 'AI Analysis Snapshot'}
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={() => setExpandedPreview(null)}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {renderExpandedContent()}
                </DialogContent>
            </Dialog>

            <Divider sx={{ borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' }} />

            {/* For Students of Classes 3-8 */}
            <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 4 }, backgroundColor: 'background.default' }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            mb: 6,
                            color: 'text.primary',
                        }}
                    >
                        For Students
                    </Typography>
                    <Grid2 container spacing={4} alignItems="center" sx={{ justifyContent: 'center' }}>
                        <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Stack spacing={2} sx={{ alignItems: { xs: 'center', md: 'flex-start' }, width: '100%', maxWidth: '400px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' }, width: '100%' }}>
                                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                        Builds good habits early
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' }, width: '100%' }}>
                                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                        Focuses on understanding
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' }, width: '100%' }}>
                                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                        No exam pressure
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Stack spacing={2} sx={{ alignItems: { xs: 'center', md: 'flex-start' }, width: '100%', maxWidth: '400px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' }, width: '100%' }}>
                                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                        No extra burden
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' }, width: '100%' }}>
                                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                        This does not replace teaching
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid2>
                    </Grid2>
                </Container>
            </Box>

            <Divider sx={{ borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' }} />

            {/* Footer */}
            <Box sx={{
                py: { xs: 2, md: 3 },
                px: { xs: 2, md: 4 },
                backgroundColor: 'background.default',
                marginBottom: '60px',
            }}>
                <Container maxWidth="lg">
                    <Stack spacing={1} alignItems="center">
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 500,
                                textAlign: 'center',
                                color: 'text.primary',
                                fontSize: { xs: '1rem', md: '1.125rem' },
                            }}
                        >
                            We are not teaching children faster.
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                textAlign: 'center',
                                color: 'text.primary',
                                fontSize: { xs: '1.5rem', md: '1.5rem' },
                            }}
                        >
                            We are helping them practice better.
                        </Typography>
                    </Stack>
                </Container>
            </Box>

            {/* Contact Footer */}
            <Box sx={{
                py: { xs: 2, md: 3 },
                px: { xs: 2, md: 4 },
                backgroundColor: '#12343B',
                color: 'white',
            }}>
                <Container maxWidth="lg">
                    <Stack spacing={2} alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                            <EmailIcon sx={{ fontSize: '1.25rem' }} />
                            <Typography
                                variant="body1"
                                sx={{
                                    textAlign: 'center',
                                    color: 'white',
                                }}
                            >
                                support@adaptprepare.ai
                            </Typography>
                        </Stack>
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            spacing={{ xs: 1, sm: 3 }} 
                            alignItems="center" 
                            justifyContent="center"
                            divider={<Box sx={{ color: 'rgba(255, 255, 255, 0.5)', display: { xs: 'none', sm: 'block' } }}>|</Box>}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'white',
                                    fontSize: '0.875rem',
                                }}
                            >
                                © 2026
                            </Typography>
                            <Typography
                                component="a"
                                href="/privacy"
                                variant="body2"
                                sx={{
                                    color: 'white',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    '&:hover': {
                                        color: 'rgba(255, 255, 255, 0.8)',
                                    },
                                }}
                            >
                                Privacy
                            </Typography>
                            <Typography
                                component="a"
                                href="/tnc"
                                variant="body2"
                                sx={{
                                    color: 'white',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    '&:hover': {
                                        color: 'rgba(255, 255, 255, 0.8)',
                                    },
                                }}
                            >
                                Terms
                            </Typography>
                        </Stack>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
};

export default Home;
